import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type {
  IApiGeneralParams,
  IApiPaginatedResponse,
  IApiResponse,
} from "@/types";
import type {
  INfcAnalytics,
  INfcChip,
  INfcChipActivity,
  INfcChipConfigurePayload,
  INfcCompanyStats,
  INfcDeviceTypeCount,
  INfcSubmission,
  IUpdateNfcSubmissionPayload,
  NFC_CHIP_STATUS,
  NFC_SUBMISSION_STATUS,
} from "@/types/(company)/nfc-forms.interface";

export const companyNfcFormsApi = createApi({
  reducerPath: "companyNfcFormsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "NfcForms",
    "NfcForm",
    "NfcSubmissions",
    "NfcSubmission",
    "NfcChips",
    "NfcChip",
  ],
  endpoints: (builder) => ({
    // ── Submissions (NFC-channel inbox) ───────────────────────────────────────
    // Form AUTHORING moved to the general forms module (`companyFormsApi`,
    // `company/forms`). This slice keeps the NFC submissions inbox + chips.
    getNfcSubmissions: builder.query<
      IApiPaginatedResponse<INfcSubmission[]>,
      IApiGeneralParams & { formId: number | string }
    >({
      query: ({ formId, ...params }) => ({
        url: APIEndpoints.Company.Nfc.Forms.ListSubmissions(formId),
        params,
      }),
      providesTags: ["NfcSubmissions"],
    }),
    getAllNfcSubmissions: builder.query<
      IApiPaginatedResponse<INfcSubmission[]>,
      IApiGeneralParams & {
        formId?: number | string;
        status?: NFC_SUBMISSION_STATUS;
        fromDate?: string;
        toDate?: string;
      }
    >({
      query: (params) => ({
        url: APIEndpoints.Company.Nfc.Forms.ListAllSubmissions,
        params,
      }),
      providesTags: ["NfcSubmissions"],
    }),
    getNfcSubmissionById: builder.query<
      IApiResponse<INfcSubmission>,
      { submissionId: number | string }
    >({
      query: ({ submissionId }) => ({
        url: APIEndpoints.Company.Nfc.Forms.GetSubmission(submissionId),
      }),
      providesTags: (_r, _e, arg) => [
        { type: "NfcSubmission", id: arg.submissionId },
      ],
    }),
    updateNfcSubmission: builder.mutation<
      IApiResponse<INfcSubmission>,
      { submissionId: number | string; payload: IUpdateNfcSubmissionPayload }
    >({
      query: ({ submissionId, payload }) => ({
        url: APIEndpoints.Company.Nfc.Forms.UpdateSubmission(submissionId),
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: (_r, _e, arg) => [
        "NfcSubmissions",
        { type: "NfcSubmission", id: arg.submissionId },
        "NfcChips",
      ],
    }),

    // ── Chips ─────────────────────────────────────────────────────────────────
    getNfcStats: builder.query<IApiResponse<INfcCompanyStats>, void>({
      query: () => ({ url: APIEndpoints.Company.Nfc.Chips.Stats }),
      providesTags: ["NfcChips", "NfcForms"],
    }),
    getNfcAnalytics: builder.query<IApiResponse<INfcAnalytics>, void>({
      query: () => ({ url: APIEndpoints.Company.Nfc.Chips.Analytics }),
      providesTags: ["NfcChips"],
    }),
    getNfcDeviceTypeCounts: builder.query<
      IApiResponse<INfcDeviceTypeCount[]>,
      void
    >({
      query: () => ({ url: APIEndpoints.Company.Nfc.Chips.DeviceTypeCounts }),
      providesTags: ["NfcChips"],
    }),
    getNfcChips: builder.query<
      IApiPaginatedResponse<INfcChip[]>,
      IApiGeneralParams & {
        status?: NFC_CHIP_STATUS;
        action?: string;
        deviceType?: string;
        attention?: string;
      }
    >({
      query: (params) => ({
        url: APIEndpoints.Company.Nfc.Chips.List,
        params,
      }),
      providesTags: ["NfcChips"],
    }),
    getNfcChipById: builder.query<
      IApiResponse<INfcChip>,
      { chipId: number | string }
    >({
      query: ({ chipId }) => ({
        url: APIEndpoints.Company.Nfc.Chips.GetById(chipId),
      }),
      providesTags: (_r, _e, arg) => [{ type: "NfcChip", id: arg.chipId }],
    }),
    getNfcChipActivity: builder.query<
      IApiResponse<INfcChipActivity>,
      { chipId: number | string }
    >({
      query: ({ chipId }) => ({
        url: APIEndpoints.Company.Nfc.Chips.Events(chipId),
      }),
      providesTags: (_r, _e, arg) => [{ type: "NfcChip", id: arg.chipId }],
    }),
    configureNfcChip: builder.mutation<
      IApiResponse<INfcChip>,
      { chipId: number | string; payload: INfcChipConfigurePayload }
    >({
      query: ({ chipId, payload }) => ({
        url: APIEndpoints.Company.Nfc.Chips.Configure(chipId),
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: (_r, _e, arg) => [
        "NfcChips",
        { type: "NfcChip", id: arg.chipId },
      ],
    }),
  }),
});

export const {
  useGetNfcSubmissionsQuery,
  useGetAllNfcSubmissionsQuery,
  useLazyGetAllNfcSubmissionsQuery,
  useGetNfcSubmissionByIdQuery,
  useUpdateNfcSubmissionMutation,
  useGetNfcStatsQuery,
  useGetNfcAnalyticsQuery,
  useGetNfcDeviceTypeCountsQuery,
  useGetNfcChipsQuery,
  useGetNfcChipByIdQuery,
  useGetNfcChipActivityQuery,
  useConfigureNfcChipMutation,
} = companyNfcFormsApi;
